<template>
  <section class="section statement-list">
    <div class="container">
      <div
        v-if="ownerGroups && ownerGroups.length > 1"
        class="columns"
      >
        <div class="column is-four-fifths">
          <router-link
            to="/statements"
            class="view-link button is-info"
            tag="button"
          >
            Back
          </router-link>
        </div>
      </div>
      <div class="columns">
        <div class="column is-four-fifths">
          <h1
            v-if="currentOwnerGroup && !currentOwnerGroup.group_name && !loading"
            class="title is-4"
          >
            {{ currentOwnerGroup.first_name }} {{ currentOwnerGroup.last_name }}
          </h1>
          <h1
            v-if="currentOwnerGroup && currentOwnerGroup.group_name && !loading"
            class="title is-4"
          >
            {{ currentOwnerGroup.group_name }}
          </h1>
        </div>
        <div class="column">
          <div class="field has-addons">
            <div class="control">
              <div class="select is-small">
                <select v-model="selectedYear">
                  <option
                    v-for="year in availableYears"
                    :key="year"
                    :value="year"
                  >
                    {{ year }}
                  </option>
                </select>
              </div>
            </div>
            <div class="control">
              <button
                :class="{ 'is-loading': downloadLoading }"
                class="button is-info is-small"
                :disabled="!hasLatestYearEnd || downloadLoading"
                @click="getLatestYearEndStatement"
              >
                <b-icon
                  icon="file-pdf"
                  size="is-small"
                />
                <span>Download Year End Statement</span>
              </button>
            </div>
          </div>
        </div>
      </div>
      <b-message
        v-if="errorMessage"
        type="is-danger"
      >
        {{ errorMessage }}
      </b-message>
      <b-table
        :loading="loading"
        :data="statements"
        :empty="!loading && !statements.length && !errorMessage"
        default-sort="processed_to"
        default-sort-direction="desc"
      >
        <b-table-column
          v-slot="props"
          field="processed_to"
          label="Month"
          width="150"
          sortable
        >
          {{ formatProcessedMonth(props.row.processed_to) }}
        </b-table-column>

        <b-table-column
          v-slot="props"
          field="completed_at"
          label="Completed"
          width="150"
          sortable
        >
          {{ formatCompletedAt(props.row.completed_at) }}
        </b-table-column>

        <b-table-column
          v-slot="props"
          field="property_name"
          label="Properties"
        >
          <div
            v-for="item in findProperties(props.row)"
            :key="item"
          >
            {{ item }}
          </div>
        </b-table-column>

        <b-table-column
          v-slot="props"
          field="opening_balance"
          label="Opening Balance"
        >
          {{ formatCurrency(props.row.opening_balance) }}
        </b-table-column>

        <b-table-column
          v-slot="props"
          field="closing_balance"
          label="Closing Balance"
        >
          {{ formatCurrency(props.row.closing_balance) }}
        </b-table-column>

        <b-table-column
          v-slot="props"
          field="pdf_key"
          numeric
        >
          <statement-download-button
            :id="props.row.id"
            :pdf-key="props.row.pdf_key"
            :owner-id="props.row.user_id"
          />
        </b-table-column>

        <template #empty>
          <section class="section">
            <div class="content has-text-grey has-text-centered">
              <p>No statements found.</p>
            </div>
          </section>
        </template>
      </b-table>
    </div>
  </section>
</template>

<script>
import format from 'date-fns/format'
import parse from 'date-fns/parse'
import Raven from 'raven-js'
import { mapGetters } from 'vuex'
import StatementDownloadButton from '@/components/StatementDownloadButton.vue'
import ApiGateway from '@/services/api-gateway'
import { getErrorMessage } from '@/services/helpers'

export default {
  name: 'StatementList',

  components: { StatementDownloadButton },

  props: {
    userId: {
      type: String,
      required: true
    }
  },

  data () {
    return {
      errorMessage: null,
      loading: true,
      downloadLoading: false,
      selectedYear: null
    }
  },

  computed: {
    ...mapGetters({
      statements: 'allStatements',
      hasLatestYearEnd: 'hasYearEndStatement',
      activeCompanyId: 'activeCompanyId',
      activeLinkedOwnerId: 'activeLinkedOwnerId',
      currentOwnerGroup: 'currentOwnerGroup',
      ownerGroups: 'ownerGroups'
    }),
    availableYears () {
      if (this.statements === null || this.statements === undefined || this.statements.length === 0) return []
      
      const years = this.statements.map(statement => {
        if (statement.processed_to === null || statement.processed_to === undefined) return null
        // Get year from date string using parse from date-fns and extracting year from Date object
        const parsedDate = parse(statement.processed_to)
        return parsedDate.getFullYear()
      }).filter(year => year !== null)
      
      // Get unique years and sort in descending order
      return [...new Set(years)].sort((a, b) => b - a)
    }
  },

  async created () {
    try {
      await this.$store.dispatch('getAllStatements', this.userId)
      // Set default selected year to the most recent one
      if (this.availableYears.length > 0) {
        this.selectedYear = this.availableYears[0]
      }
    } catch (e) {
      Raven.captureException(e)
      this.errorMessage = getErrorMessage(e)
    }
    this.loading = false
  },

  methods: {
    formatProcessedMonth (processedTo) { return format(parse(processedTo), 'MMMM YYYY') },
    formatCompletedAt (completedAt) { return format(parse(completedAt), 'MMM D, YYYY') },
    formatCurrency (amount) { return this.$store.getters.formattedCurrencyAmount(amount) },
    findProperties (statement) {
      // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
      const names = statement.properties_owned?.map(property => property.name) || []
      // For legacy statements without `properties_owned` attribute, fall back to owner_payments to find property name (if any)
      if (names.length === 0) {
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        const properties = statement.line_items?.owner_payments?.properties || []
        for (const property of properties) {
          names.push(property.property_name)
        }
      }
      return names
    },
    async getLatestYearEndStatement () {
      this.pdfWindow = window.open('', '_blank')
      this.downloadLoading = true
      try {
        const response = await ApiGateway.invokeApi({
          method: 'GET',
          pathTemplate: '/companies/{companyId}/owners/{userId}/year-end-statement?year={year}&owner_id={ownerId}',
          params: {
            companyId: this.activeCompanyId,
            userId: this.activeLinkedOwnerId,
            ownerId: this.userId,
            year: this.selectedYear
          }
        }) // Open blank window on `click` event to play nice with popup blockers.
        this.pdfWindow.location = response.data
        this.pdfWindow.focus()
      } catch (e) {
        Raven.captureException(e)
        this.errorMessage = getErrorMessage(e)
      } finally {
        this.downloadLoading = false
      }
    }
  }
}
</script>
